import { useState, useEffect } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { ShieldCheck, ArrowRight, PartyPopper, Building2, AlertTriangle } from "lucide-react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { PasswordField, isPasswordValid } from "@/components/PasswordField";
import { useAuth } from "@/context/AuthContext";
import { useSeo } from "@/hooks/useSeo";

export default function CompleteRegistration() {
  useSeo("Registrierung abschließen – LAIzyDocu", "Schließen Sie Ihre LAIzyDocu-Registrierung ab und starten Sie Ihren Testzeitraum.");
  const { completeRegistration } = useAuth();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const token = params.get("token") || "";

  const [checking, setChecking] = useState(true);
  const [valid, setValid] = useState(false);
  const [email, setEmail] = useState("");
  const [practiceName, setPracticeName] = useState("");
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(null);

  useEffect(() => {
    let active = true;
    (async () => {
      if (!token) { setChecking(false); setValid(false); return; }
      try {
        const { data } = await api.get("/trial/validate-token", { params: { token } });
        if (active) { setValid(true); setEmail(data.email); }
      } catch { if (active) setValid(false); }
      finally { if (active) setChecking(false); }
    })();
    return () => { active = false; };
  }, [token]);

  const submit = async (e) => {
    e.preventDefault();
    if (!isPasswordValid(password)) { toast.error("Das Passwort erfüllt nicht alle Anforderungen."); return; }
    if (password !== confirm) { toast.error("Die Passwörter stimmen nicht überein."); return; }
    setLoading(true);
    try {
      const data = await completeRegistration(token, practiceName.trim(), password);
      setSuccess(data);
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Registrierung fehlgeschlagen."); }
    finally { setLoading(false); }
  };

  if (checking) {
    return <div className="min-h-screen bg-stone-50 grid place-items-center text-stone-400">Lädt…</div>;
  }

  if (!valid) {
    return (
      <div className="min-h-screen bg-stone-50 grid place-items-center px-6">
        <div data-testid="complete-invalid" className="w-full max-w-md bg-white rounded-3xl border border-stone-100 shadow-[0_8px_30px_rgb(0,0,0,0.04)] p-8 md:p-10 text-center">
          <div className="w-16 h-16 rounded-2xl bg-orange-50 text-orange-600 grid place-items-center mx-auto mb-6"><AlertTriangle className="w-8 h-8" /></div>
          <h1 className="font-display text-2xl font-semibold text-emerald-900">Link ungültig oder abgelaufen</h1>
          <p className="text-stone-600 mt-3">Dieser Registrierungslink ist nicht (mehr) gültig. Bitte fordern Sie einen neuen Testzugang an.</p>
          <Link to="/testzugang" data-testid="complete-new-request" className="inline-block mt-6">
            <Button className="h-12 rounded-xl bg-emerald-900 hover:bg-emerald-800 text-white">Neuen Testzugang anfordern</Button>
          </Link>
        </div>
      </div>
    );
  }

  if (success) {
    return (
      <div className="min-h-screen bg-stone-50 grid place-items-center px-6 py-10">
        <div data-testid="complete-success" className="w-full max-w-md bg-white rounded-3xl border border-stone-100 shadow-[0_8px_30px_rgb(0,0,0,0.04)] p-8 md:p-10 text-center">
          <div className="w-16 h-16 rounded-2xl bg-emerald-50 text-emerald-700 grid place-items-center mx-auto mb-6"><PartyPopper className="w-8 h-8" /></div>
          <h1 className="font-display text-3xl font-semibold text-emerald-900">Willkommen, {success.practice_name}!</h1>
          <p className="text-stone-600 mt-3 leading-relaxed">Ihr Konto ist aktiv. Ihr Testzeitraum läuft <strong>{success.trial_days} Tage</strong> – bis zum <strong>{success.trial_ends_human}</strong>.</p>
          <p className="text-emerald-800 font-medium mt-4">Wir wünschen Ihnen viel Spaß! 🎉</p>
          <Button onClick={() => navigate("/admin", { replace: true })} data-testid="complete-go-dashboard"
            className="w-full h-14 mt-7 rounded-xl bg-emerald-900 hover:bg-emerald-800 text-white">Zum Dashboard <ArrowRight className="ml-2 w-5 h-5" /></Button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-stone-50 grid place-items-center px-6 py-10">
      <div className="w-full max-w-md bg-white rounded-3xl border border-stone-100 shadow-[0_8px_30px_rgb(0,0,0,0.04)] p-8 md:p-10">
        <Link to="/" className="inline-flex items-center gap-2 mb-6">
          <div className="w-10 h-10 rounded-xl bg-emerald-900 text-white grid place-items-center"><ShieldCheck className="w-5 h-5" /></div>
          <span className="font-display text-lg font-semibold text-emerald-900">LA<span className="text-emerald-600">I</span>zyDoc</span>
        </Link>
        <h1 className="font-display text-3xl font-semibold text-emerald-900">Registrierung abschließen</h1>
        <p className="text-stone-500 mt-2 mb-6">Für <span className="font-medium text-stone-800">{email}</span></p>

        <form onSubmit={submit} className="space-y-5">
          <div>
            <Label htmlFor="practice-name" className="text-base font-medium text-stone-800">Name der Praxis</Label>
            <div className="relative mt-2">
              <Building2 className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-stone-400" />
              <Input id="practice-name" data-testid="complete-practice-name" required minLength={2} value={practiceName}
                onChange={(e) => setPracticeName(e.target.value)} className="h-14 pl-12 pr-4 text-lg rounded-xl" />
            </div>
          </div>
          <PasswordField value={password} onChange={setPassword} label="Passwort" testid="complete-password" />
          <div>
            <Label htmlFor="complete-confirm" className="text-base font-medium text-stone-800">Passwort wiederholen</Label>
            <Input id="complete-confirm" data-testid="complete-confirm" type="password" value={confirm}
              onChange={(e) => setConfirm(e.target.value)} autoComplete="new-password" className="h-14 px-4 text-lg rounded-xl mt-2" />
            {confirm && confirm !== password && <p className="text-xs text-orange-600 mt-1">Passwörter stimmen nicht überein.</p>}
          </div>
          <Button type="submit" data-testid="complete-submit" disabled={loading}
            className="w-full h-16 text-lg rounded-xl bg-emerald-900 hover:bg-emerald-800 text-white">
            {loading ? "Wird erstellt…" : "Registrierung abschließen"}
          </Button>
        </form>
      </div>
    </div>
  );
}
